import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import { countries } from "countries-list";
import SelectPartner from "../../components/ui/SelectPartner";
import { useNotification } from "../../../../context/NotificationContext";
import { renderDate } from "../../utils/dateFormatter";

const statusColors = {
  reserved: "#3498db",
  checked_in: "#27ae60",
  checked_out: "#8e44ad",
  cancelled: "#e74c3c",
  noshow: "#f39c12",
};

const UpdateGuestInfoPopup = ({ bookingId, onSubmit, bookingData }) => {
  const [formValues, setFormValues] = useState({
    fullName: "",
    nationality: "",
    phoneNumber: "",
    email: "",
    expectedArrivalTime: null,
    gstCompanyName: "",
    gstCompanyAddress: "",
    gstNumber: "",
    additionalNotes: "",
    partner: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const submitButtonRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const { notify } = useNotification();

  // Time options for Expected Arrival Time dropdown
  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const formatHour = (h) => {
      const period = h < 12 ? "AM" : "PM";
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${hour12} ${period}`;
    };
    return {
      value: `${formatHour(i)} - ${formatHour((i + 1) % 24)}`,
      label: `${formatHour(i)} - ${formatHour((i + 1) % 24)}`,
    };
  });

  const countryOptions = Object.entries(countries)
    .map(([code, country]) => ({ value: country.name, label: country.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Pre-fill form values from bookingData
  useEffect(() => {
    if (bookingData) {
      const expectedTime =
        bookingData.expected_time === "N/A" ? null : bookingData.expected_time;
      setFormValues({
        fullName: bookingData.name === "N/A" ? "" : bookingData.name || "",
        nationality:
          bookingData.nationality === "N/A" ? "" : bookingData.nationality || "",
        phoneNumber: bookingData.phone === "N/A" ? "" : bookingData.phone || "",
        email: bookingData.email === "N/A" ? "" : bookingData.email || "",
        expectedArrivalTime: expectedTime
          ? timeOptions.find((t) => t.value === expectedTime) || null
          : null,
        gstCompanyName:
          bookingData.gst_company_name === "N/A"
            ? ""
            : bookingData.gst_company_name || "",
        gstCompanyAddress:
          bookingData.gst_company_address === "N/A"
            ? ""
            : bookingData.gst_company_address || "",
        gstNumber:
          bookingData.gst_number === "N/A" ? "" : bookingData.gst_number || "",
        additionalNotes:
          bookingData.additional_notes === "N/A"
            ? ""
            : bookingData.additional_notes || "",
        partner: bookingData.bookingSource
          ? { value: bookingData.bookingSource, name: bookingData.partnerName || "N/A" }
          : null,
      });
    }
  }, [bookingData]);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    let validationErrors = {};
    if (!formValues.fullName.trim()) {
      validationErrors.fullName = "Full name is required";
    }
    if (!formValues.nationality.trim()) {
      validationErrors.nationality = "Nationality is required";
    }
    if (!formValues.phoneNumber.trim()) {
      validationErrors.phoneNumber = "Phone number is required";
    } else if (!/^\+?[\d\s-]{8,}$/.test(formValues.phoneNumber)) {
      validationErrors.phoneNumber = "Invalid phone number format";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("error", "Please fix the validation errors");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        bookingId: bookingId,
        fullName: formValues.fullName,
        nationality: formValues.nationality,
        phoneNumber: formValues.phoneNumber,
        email: formValues.email,
        expectedArrivalTime: formValues.expectedArrivalTime
          ? formValues.expectedArrivalTime.value
          : null,
        gstCompanyName: formValues.gstCompanyName || null,
        gstCompanyAddress: formValues.gstCompanyAddress || null,
        gstNumber: formValues.gstNumber || null,
        additionalNotes: formValues.additionalNotes || null,
        guest_id: bookingData?.guest_id,
        partnerId: formValues.partner ? formValues.partner.value : null,
        partnerName: formValues.partner ? formValues.partner.name : null,
      };

      console.log("Submitting payload:", payload);

      const response = await axios.put(`${apiUrl}/booking/update-guest-info`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", "Guest information updated successfully!");
      if (onSubmit) onSubmit(response.data);
      if (submitButtonRef.current) submitButtonRef.current.click();
    } catch (error) {
      notify("error", "Failed to update guest information. Please try again.");
      console.error("Error updating guest info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const isReserved = bookingData?.bookingStatus === "reserved";
  const isCheckedOutOrCancelledOrNoShow = ["checked_out", "cancelled", "noshow"].includes(
    bookingData?.bookingStatus
  );

  const nextBooking =
    bookingData?.nextBookings?.find((b) => b.roomNumber === bookingData.roomNumber) || null;

  const statusColor = statusColors[bookingData?.bookingStatus] || "#7f8c8d";

  return (
    <div id="update_guest_info" className="modal custom-modal fade" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            {/* Header */}
            <div
              className="modal-header"
              style={{ backgroundColor: "#f0f8ff", borderBottom: "1px solid #e0e7ff" }}
            >
              <h5 className="modal-title" style={{ fontWeight: "600", color: "#333" }}>
                Update Guest Information
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleCancel} />
            </div>

            {/* Body */}
            <div className="modal-body" style={{ overflowY: "auto" }}>
              <div className="row g-2">
                {/* Guest Details */}
                <div className="col-12 mb-2">
                  <div
                    className="card"
                    style={{
                      border: "1px solid #e0e7ff",
                      borderRadius: "6px",
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <div className="row g-0">
                      <div className="col-6 pe-2">
                        <p><strong>Folio No:</strong> {bookingData?.bookingNumber || "N/A"}</p>
                        <p><strong>Arrival Date:</strong> {bookingData?.checkIn ? renderDate(bookingData.checkIn) : "N/A"}</p>
                        <p><strong>Departure Date:</strong> {bookingData?.checkOut ? renderDate(bookingData.checkOut) : "N/A"}</p>
                      </div>
                      <div className="col-6 ps-2">
                        <p><strong>Booking Date:</strong> {bookingData?.reservationDate ? renderDate(bookingData.reservationDate) : "N/A"}</p>
                        <p><strong>Room Number:</strong> {bookingData?.roomNumber || "N/A"}</p>
                        <p><strong>Pax:</strong> {`${bookingData?.guests?.adults || 0} Adults, ${bookingData?.guests?.children || 0} Children`}</p>
                        <p>
                          <strong>Status:</strong>{" "}
                          <span
                            style={{
                              backgroundColor: statusColor,
                              color: "#fff",
                              padding: "4px 10px",
                              borderRadius: "16px",
                              fontSize: "12px",
                              fontWeight: 600,
                              textTransform: "capitalize",
                              marginLeft: "6px",
                            }}
                          >
                            {bookingData?.bookingStatus || "N/A"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Editable Guest Info */}
                <div className="col-12">
                  <div className="card p-3">
                    <h6 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
                      Update Guest Information
                    </h6>

                    <div className="mb-2">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={formValues.fullName}
                        onChange={(e) => handleChange("fullName", e.target.value)}
                        className={`form-control ${errors.fullName ? "is-invalid" : ""}`}
                      />
                      {errors.fullName && <div className="invalid-feedback d-block">{errors.fullName}</div>}
                    </div>

                    <div className="mb-2">
                      <label>Nationality</label>
                      <Select
                        options={countryOptions}
                        value={countryOptions.find((c) => c.value === formValues.nationality) || null}
                        onChange={(opt) => handleChange("nationality", opt?.value || "")}
                        placeholder="Select nationality"
                        isClearable
                      />
                    </div>

                    <div className="mb-2">
                      <label>Phone Number</label>
                      <input
                        type="text"
                        value={formValues.phoneNumber}
                        onChange={(e) => handleChange("phoneNumber", e.target.value)}
                        className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                      />
                      {errors.phoneNumber && <div className="invalid-feedback d-block">{errors.phoneNumber}</div>}
                    </div>

                    <div className="mb-2">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formValues.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        className="form-control"
                      />
                    </div>

                    <div className="mb-2">
                      <label>Partner (Source)</label>
                      <SelectPartner
                        selectedValue={formValues.partner}
                        onSelectPartner={(p) => handleChange("partner", p)}
                        isDisabled={isCheckedOutOrCancelledOrNoShow || loading}
                      />
                    </div>

                    {isReserved && (
                      <div className="mb-2">
                        <label>Expected Arrival Time</label>
                        <Select
                          options={timeOptions}
                          value={formValues.expectedArrivalTime}
                          onChange={(option) => handleChange("expectedArrivalTime", option)}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Company Info */}
                <div className="col-12 mt-2">
                  <div className="card p-3">
                    <label>Company Name</label>
                    <input
                      type="text"
                      value={formValues.gstCompanyName}
                      onChange={(e) => handleChange("gstCompanyName", e.target.value)}
                      className="form-control mb-2"
                    />

                    <label>Company Address</label>
                    <textarea
                      value={formValues.gstCompanyAddress}
                      onChange={(e) => handleChange("gstCompanyAddress", e.target.value)}
                      className="form-control mb-2"
                      rows="3"
                    />

                    <label>GST Number</label>
                    <input
                      type="text"
                      value={formValues.gstNumber}
                      onChange={(e) => handleChange("gstNumber", e.target.value)}
                      className="form-control"
                    />
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="col-12 mt-2">
                  <div className="card p-3">
                    <label>Additional Notes</label>
                    <textarea
                      value={formValues.additionalNotes}
                      onChange={(e) => handleChange("additionalNotes", e.target.value)}
                      className="form-control"
                      rows="3"
                    />
                  </div>
                </div>

                {/* Next Booking */}
                {nextBooking && (
                  <div className="col-12 mt-2">
                    <div className="card p-3" style={{ backgroundColor: "#fff3e6" }}>
                      <h6 style={{ fontSize: 15, fontWeight: 600 }}>
                        Next Booking for Room {nextBooking.roomNumber}
                      </h6>
                      <p><strong>Arrival:</strong> {nextBooking.checkIn ? renderDate(nextBooking.checkIn) : "N/A"}</p>
                      <p><strong>Departure:</strong> {nextBooking.checkOut ? renderDate(nextBooking.checkOut) : "N/A"}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ backgroundColor: "#f0f8ff" }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <div style={{ display: "none" }}>
                <button ref={submitButtonRef} data-bs-dismiss="modal" type="reset">
                  Submit2
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateGuestInfoPopup;
